import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
  classMap,
} from '@hh/global.js';

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    sideButtons: {
      type: Boolean,
    },
    sideButtonsOnMobile: {
      type: Boolean,
    },
    paginationButtons: {
      type: Boolean,
      attribute: 'pagination-buttons',
    },
    hideCounter: {
      type: Boolean,
    },
    hideScroll: {
      type: Boolean,
    },
    rightOverflow: {
      type: Boolean,
    },
    _scroll: {
      state: true,
    },
    _hasScroll: {
      state: true,
    },
    _itemWidth: {
      state: true,
    },
    _showNext: {
      type: Boolean,
      state: true,
    },
    _showPrev: {
      type: Boolean,
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this._scroll = 0
    this._itemWidth = 1

    this.imagesPreloaded = false

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length
    
    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      this.toggleAttribute('is-product-media', this.classList.contains('product-media__grid'))

      window.addEventListener('resize', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }

    this.updateUI()
  }

  updateUI() {
    this._hasScroll = this.hasScroll()
    if (!this._total) {
      return
    }

    this._itemWidth = this._total > 0 ? this.items[0].clientWidth : 1
    this._current =
      Math.round(
        this.$container?.value?.scrollLeft / this.items[0].clientWidth
      ) + 1

    if (!this.items || this.items.length === 0) {
      this._showPrev = false
      this._showNext = false
      return
    }

    setTimeout(() => {
      this._showPrev = this.shouldShowPrev()
      this._showNext = this.shouldShowNext()
    }, 100)
  }

  preloadImages() {
    if (this.imagesPreloaded) return

    const images = Array.from(this.querySelectorAll('img'))
    images.forEach((image) => {
      image.setAttribute('loading', 'eager')
    })

    this.imagesPreloaded = true
  }

  firstUpdated() {
    this.updateUI()

    if (window.matchMedia(`(max-width: ${this.breakpoint}px)`).matches) {
      this.preloadImages()
    }

    window
      .matchMedia(`(min-width: ${this.breakpoint}px)`)
      .addEventListener('change', (e) => {
        if (e.matches) this.preloadImages()
      })
  }

  shouldShowPrev() {
    if (!this.items || this.items.length === 0) {
      return false
    }

    const slider = this.$container?.value
    if (!slider) {
      return false
    }

    const minimumScroll = 0
    const currentScroll = slider.scrollLeft
    return currentScroll > minimumScroll
  }

  shouldShowNext() {
    if (!this.items || this.items.length === 0) {
      return false
    }

    const slider = this.$container?.value
    if (!slider) {
      return false
    }

    const columnSeparation = parseInt(
      getComputedStyle(slider)?.columnGap?.replace('px', '') || 0
    )
    const itemsWidth =
      this.items.reduce((acc, item) => item.clientWidth + acc, 0) +
      columnSeparation * (this.items.length - 1)

    if (itemsWidth < 1) {
      return false
    }

    return (
      Math.ceil(slider.scrollLeft + slider.offsetWidth) < slider.scrollWidth
    )
  }

  move(event) {
    event.preventDefault()
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })

    setTimeout(() => {
      this._scroll = slideScrollPosition
    }, 300)
  }

  onPaginationClick(e, item) {
    e.preventDefault()
    if (!item) return
    // scrollIntoView causes vertical scrolling to happen when clicking on pagination which is not ideal for desktop experience of slider
    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })

    // This way prevents vertical scrolling, but it a bit more complex by manually animating the scroll instead of relying on scrollIntoView smooth scrolling behavior, also it scrolls faster when going far distances.
    // I'll disable but leave in if client wants to change to this to prevent vertical scrolling on desktop
    /*
    const container = this.$container?.value
    const target = item.offsetLeft

    this.smoothHorizontalScroll(container, target, 800)
    */
  }

  smoothHorizontalScroll(element, target, duration) {
    const start = element.scrollLeft
    const change = target - start
    const startTime = performance.now()

    // Perform the first step of the animation immediately
    this.animateScroll(startTime, start, change, duration, element)

    // Then continue the animation with requestAnimationFrame
    requestAnimationFrame(() =>
      this.animateScroll(startTime, start, change, duration, element)
    )
  }

  animateScroll(startTime, start, change, duration, element) {
    const currentTime = performance.now() - startTime
    const val = this.linear(currentTime, start, change, duration)
    element.scrollLeft = val
    if (currentTime < duration) {
      requestAnimationFrame(() =>
        this.animateScroll(startTime, start, change, duration, element)
      )
    }
  }

  linear(t, b, c, d) {
    return (c * t) / d + b
  }

  reachedEnd() {
    const slider = this.$container?.value
    if (!slider) {
      return true
    }

    const itemsWidth = this.items
      ? this.items.reduce((acc, item) => item.clientWidth + acc, 0)
      : 0
    if (itemsWidth < 1 || itemsWidth <= slider.clientWidth) {
      return true
    }

    return slider.scrollLeft === slider.scrollWidth - slider.clientWidth
  }

  hasScroll() {
    const slider = this.$container?.value
    if (!slider) {
      return false
    }

    return slider.scrollWidth > slider.clientWidth
  }

  paginationTemplate() {
    return html`
      <div class="pagination-container">
        ${this.paginationButtonsTemplate('prev')}
        <ol
          class=${classMap({
            counter: true,
            'counter--hidden': this.hideCounter,
          })}
        >
          ${(this._total > 1 &&
            this.items.map((item, idx) => {
              const isVideo = item?.hasAttribute('data-is-video')
              return html`
                <li>
                  <button
                    @click=${(e) => this.onPaginationClick(e, item)}
                    class=${classMap({
                      current: this._current == idx + 1,
                      video: isVideo,
                    })}
                    aria-label="${t('accessibility.go_to')} ${t(
                      'accessibility.image'
                    )} ${idx + 1}"
                  >
                    ${isVideo ? unsafeHTML(paths.playExternalOutline) : ''}
                  </button>
                </li>
              `
            })) ||
          ''}
          <slot name="zoom"></slot>
        </ol>
        ${this.paginationButtonsTemplate('next')}
      </div>
    `
  }

  paginationButtonsTemplate(direction) {
    if (!this.paginationButtons) return ''

    const directionMap = {
      prev: {
        ariaLabel: t('accessibility.previous_slide', 'Previous'),
        name: 'previous',
        refVal: this.$prev,
      },
      next: {
        ariaLabel: t('accessibility.next_slide', 'Next'),
        name: 'next',
        refVal: this.$next,
      },
    }

    const isPrev = direction === 'prev'
    const { ariaLabel, name, refVal } = directionMap[direction] || {}
    const classes = 'button button--pagination'

    return html`
      <button
        ${ref(refVal)}
        @click=${this.move}
        type="button"
        class=${classMap({
          [classes]: true,
          'button--prev': isPrev,
          'button--next': !isPrev,
        })}
        name=${name}
        aria-label=${ariaLabel}
        ?disabled=${direction === 'prev' ? !this._showPrev : !this._showNext}
      >
        ${unsafeHTML(paths.caret)}
      </button>
    `
  }

  render() {
    return html`
      <div
        class=${classMap({
          container: true,
          'container--hidden-scroll': this.hideScroll,
          'container--right-overflow': this.rightOverflow,
          'container--start-align': this._hasScroll,
        })}
        ${ref(this.$container)}
        role="list"
      >
        <slot name="item"></slot>
      </div>
      <div
        class=${classMap({
          buttons: true,
          'buttons--side': this.sideButtons,
          'buttons--side__mobile': this.sideButtonsOnMobile,
        })}
      >
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class=${classMap({
            button: true,
            'button--prev': true,
            'button--side': this.sideButtons,
            'button--side__mobile': this.sideButtonsOnMobile,
          })}
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this.sideButtons || this.sideButtonsOnMobile
            ? !this._showPrev
            : true}"
        >
          ${unsafeHTML(paths.caret)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class=${classMap({
            button: true,
            'button--next': true,
            'button--side': this.sideButtons,
            'button--side__mobile': this.sideButtonsOnMobile,
          })}
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this.sideButtons || this.sideButtonsOnMobile
            ? !this._showNext
            : true}"
        >
          ${unsafeHTML(paths.caret)}
        </button>

        <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
        ${this.paginationTemplate()}
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host *,
      :host *::before,
      :host *::after {
        box-sizing: border-box;
      }
      :host([active='true']) {
        position: relative;
      }
      :host .container::-webkit-scrollbar {
        display: none;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      .container--hidden-scroll {
        -ms-overflow-style: none;
        column-gap: var(--hh-spacing-sm);
        scrollbar-width: none;
      }

      .container--hidden-scroll::-webkit-scrollbar {
        display: none;
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }

      :host([active='true']) ::slotted(*) {
        width: var(--hh-cell-width) !important;
      }

      :host([active='true']) ::slotted(span) {
        line-height: 0;
      }

      .buttons {
        align-items: center;
        justify-content: center;
      }
      
      :host([is-product-media]) .buttons {
        display: block;
        position: relative;
        z-index: 20;
      }
      :host(:not([is-product-media])) .buttons {
        display: none;
      }

      .buttons--side {
        background: none;
        border: none;
        position: static;
      }

      .pagination-container {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: -2rem 0 0;
        z-index: 2;
        gap: var(--hh-spacing-xs);
      }

      @media screen and (min-width: 1024px) {
        .pagination-container {
          margin: -6rem 0 0;
        }
      }

      .counter {
        display: flex;
        align-items: center;
        background-color: rgba(var(--hh-color-day-rgb), 0.9);
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
        border-radius: 33px;
        padding: calc(var(--hh-spacing-xxs) + 0.1rem) var(--hh-spacing-sm);
        list-style: none;
      }

      .counter button {
        font-size: 0;
        appearance: none;
        border: none;
        background: none;
        cursor: pointer;
        margin: 0;
        padding: 0;
        display: flex;
      }
      .counter button:not(.zoom):not(.video) {
        padding: 0.4rem;
      }
      .counter button:not(.zoom):before {
        content: '';
        width: 0.5rem;
        height: 0.5rem;
        display: block;
        opacity: 0.5;
        background-color: var(--hh-color-blue);
        border-radius: 100%;
      }
      .counter button.current:before {
        opacity: 0.9;
      }

      .counter button.video::before {
        display: none;
      }

      .counter button.video {
        display: flex;
        width: 1.4rem;
        height: 1.4rem;
        margin: 0 0.4rem;
        color: var(--hh-color-blue);
        opacity: 0.5;
      }

      .counter button.current.video {
        opacity: 1;
      }

      .counter button.video svg {
        flex: 1 0 auto;
      }

      .zoom--button {
        appearance: none;
        padding: 0;
        line-height: 0;
        border: 0;
        background: transparent;
      }

      .counter--hidden {
        opacity: 0;
        position: absolute;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.8rem;
        pointer-events: none;
      }

      .button--prev {
        transform: rotate(90deg);
      }

      .button--next {
        transform: rotate(-90deg);
      }

      .button--side,
      .button--side__mobile {
        border-radius: 50%;
        filter: drop-shadow(
          0px 0.8533334732055664px 12.80000114440918px rgba(0, 0, 0, 0.05)
        );
        height: var(--hh-spacing-xl);
        padding: 0.2rem 0 0;
        position: absolute;
        top: 50%;
        width: var(--hh-spacing-xl);
        z-index: 2;
      }

      .button--side.button--next,
      .button--side__mobile.button--next {
        right: 0;
        transform: translateY(-50%) rotate(-90deg);
      }

      .button--side.button--prev,
      .button--side__mobile.button--prev {
        left: 0;
        transform: translateY(-50%) rotate(90deg);
      }

      .button--side:disabled,
      .button--side__mobile:disabled {
        display: none;
        pointer-events: none;
      }

      .button--pagination {
        display: none;
        border-radius: var(--hh-spacing-2xl);
        color: rgba(var(--hh-color-blue-rgb), 0.5);
        width: var(--hh-spacing-xl);
        height: var(--hh-spacing-xl);
        background-color: rgba(var(--hh-color-day-rgb), 0.9);
        box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
      }

      @media screen and (min-width: 1024px) {
        .button--pagination {
          display: block;
        }
      }

      .button--pagination:disabled {
        pointer-events: none;
        cursor: default;
        color: rgba(var(--hh-color-blue-rgb), 0.3);
      }

      @media screen and (min-width: 1024px) {
        .container--hidden-scroll {
          justify-content: center;
        }

        .container--start-align {
          justify-content: start;
        }

        .buttons--side__mobile .button {
          display: none;
        }

        .button--side.button--next {
          right: calc(-1 * var(--hh-spacing-2xl));
        }

        .button--side.button--prev {
          left: calc(-1 * var(--hh-spacing-2xl));
        }
      }

      @media screen and (max-width: 1023px) {
        .buttons--side .button {
          display: none;
        }

        .buttons--side__mobile .button {
          display: block;
        }

        .buttons--side__mobile .pagination-container {
          display: none;
        }

        .container--right-overflow {
          margin-right: -4.5%;
          width: auto;
        }
      }

      @media screen and (min-width: 1280px) {
        .button--side.button--next {
          right: -4rem;
        }

        .button--side.button--prev {
          left: -4rem;
        }
      }
    `,
  ]
}

if (!customElements.get('hh-slider')) {
  customElements.define('hh-slider', HHSlider);
}
